import { InputTextArea } from "@/components";
import { BackAppBar } from "@/components/ui";
import useCaseMutation from "@/hooks/useCaseMutation";
import { QUERY_KEYS } from "@/libraries/constant";
import { getCaseQueryOptions } from "@/queries";
import { DataIdSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import SendIcon from "@mui/icons-material/Send";
import IconButton from "@mui/material/IconButton";
import { onlineManager, useSuspenseQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import omit from "lodash/omit";
import { useMemo } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

const CaseInfoFormSchema = z.object({
	description: z.string().min(1, "Description is required"),
});

export const Route = createFileRoute(
	"/dashboard/case/view/$id/case-info/$type",
)({
	params: {
		parse: (params: any) => ({
			id: DataIdSchema.parse(Number(params.id)),
			type: z
				.enum(["summaries", "allergies", "medication"])
				.default("summaries")
				.parse(params.type),
		}),
		stringify: ({ id, type }) => ({ id: id.toString(), type }),
	},
	loader: ({ context, params }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return context.queryClient.ensureQueryData(
				getCaseQueryOptions(params.id),
			);
		}
		return context.queryClient.getQueryData([QUERY_KEYS.GET_CASE, params.id]);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const caseId = Route.useParams({
		select: (s) => s.id,
	});
	const summaryType = Route.useParams({
		select: (s) => s.type,
	});
	const caseQuery = useSuspenseQuery(getCaseQueryOptions(caseId));
	const title = useMemo(() => {
		if (!caseQuery.data.patient) return caseQuery.data.title;
		return [caseQuery.data.patient.name, caseQuery.data.patient.surname].join(
			" ",
		);
	}, [caseQuery.data]);
	const subtitle = useMemo<string>(() => {
		switch (summaryType) {
			case "summaries":
				return "Case Summary";
			case "allergies":
				return "Allergies";
			case "medication":
				return "Medication";
			default:
				return "";
		}
	}, [summaryType]);

	const {
		control,
		reset,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<z.infer<typeof CaseInfoFormSchema>>({
		mode: "all",
		resolver: zodResolver(CaseInfoFormSchema),
	});

	const { addCaseInfo } = useCaseMutation();

	const onSubmit: SubmitHandler<z.infer<typeof CaseInfoFormSchema>> = (
		data,
		e,
	) => {
		e?.stopPropagation();
		e?.preventDefault();
		addCaseInfo.mutate({
			caseId,
			info_type: summaryType,
			...data,
		});
		return reset();
	};

	return (
		<>
			<BackAppBar
				className="!bg-gradient-account [&_*_]:text-white"
				title={title}
				subTitle={subtitle}
			/>
			<Outlet />
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="bg-white container py-2 flex items-center gap-2"
			>
				<Controller
					control={control}
					name="description"
					defaultValue=""
					render={({ field }) => (
						<InputTextArea
							fullWidth
							autoFocus
							size="small"
							placeholder={`Type the latest ${subtitle.toLowerCase()}`}
							aria-label={`Type the latest ${subtitle.toLowerCase()}`}
							maxRows={4}
							{...omit(field, ["ref"])}
						/>
					)}
				/>
				<IconButton
					type="submit"
					size="large"
					color="primary"
					disabled={!isValid || !isDirty || isSubmitting}
				>
					<SendIcon />
				</IconButton>
			</form>
		</>
	);
}
